import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Descriptions,
  Select,
  Space,
  Dropdown,
  Checkbox,
  Menu,
} from "antd";
import { SettingOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Option } = Select;

const CampaignsTable = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [etablissementFilter, setEtablissementFilter] = useState(null);
  const [columnsVisible, setcolumnsVisible] = useState(false);

  const [uniqueEtablissements, setUniqueEtablissements] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({});

  // Transform Post data to campaign format
  const transformPostsToCampaigns = (posts) => {
    const campaignsMap = new Map();
    const etablissements = new Set();

    posts.forEach((post) => {
      // Check if the post has an etablissement and campagnes
      if (post.etablissement && post.etablissement.campagnes) {
        post.etablissement.campagnes.forEach((campaign) => {
          // Add établissement to unique list
          etablissements.add(post.etablissement.name);

          const campaignKey = `${post.etablissement._id}-${
            campaign.name || "default"
          }`;

          // Calculate follower gains
          const initialFollowers = 0; // You would need to get this from somewhere
          const currentFollowers = parseInt(
            post.userId.nbFollowersInsta?.replace(/,/g, "") || 0
          );
          const followersGained = currentFollowers - initialFollowers;

          if (!campaignsMap.has(campaignKey)) {
            campaignsMap.set(campaignKey, {
              key: campaignKey,
              etablissementName: post.etablissement.name,
              name:
                campaign.name || post.selectedCampaignName || "Campagne Test",
              platform: campaign.type || "Instagram",
              status: campaign.status,
              startDate: campaign.dateDebut,
              endDate: campaign.dateFin,
              totalRewardBudget: campaign.budget || 0,
              consumedBudget: 0, // Will be calculated with rewards
              realCost: 0, // Will be calculated with rewards

              // Aggregate metrics from posts
              postsCount: 1,
              totalLikes: parseInt(post.likes?.replace(/,/g, "") || 0),
              totalComments: parseInt(post.comments?.replace(/,/g, "") || 0),
              followersGained: followersGained,

              description: post.etablissement.description,
              accessConditions: campaign.mentionsHashTags,
              nbfollowersMin: campaign.nbfollowersMin,

              recompenseOption: campaign.recompenseOption,
              scoring: campaign.scoring,

              // Track posts for this campaign
              posts: [post],
            });
          } else {
            const existingCampaign = campaignsMap.get(campaignKey);
            existingCampaign.postsCount += 1;
            existingCampaign.totalLikes += parseInt(
              post.likes?.replace(/,/g, "") || 0
            );
            existingCampaign.totalComments += parseInt(
              post.comments?.replace(/,/g, "") || 0
            );
            existingCampaign.followersGained += followersGained;
            existingCampaign.posts.push(post);
          }
        });
      }
    });

    // Mettre à jour le tableau des campagnes avec les récompenses
    return Array.from(campaignsMap.values());
  };

  const user = JSON.parse(localStorage.getItem("user"));

  // Fetch campaigns from Posts API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await axios.get("https://trend-me.com/api/Post");
        const transformedCampaigns = transformPostsToCampaigns(response.data);

        // Fetch rewards data
        const rewardsResponse = await fetch("https://trend-me.com/rewards");
        const rewardsData = await rewardsResponse.json();
        setRewards(rewardsData);

        // Calculate consumed budget and real cost with rewards data
        const campaignsWithRewards = transformedCampaigns.map((campaign) => {
          // Filter rewards for this campaign
          const campaignRewards = rewardsData.filter(
            (reward) => reward.postId?.selectedCampaignName === campaign.name
          );

          // Calculate consumed budget - based on the reward value
          let consumedBudget = 0;
          campaignRewards.forEach((reward) => {
            consumedBudget += parseInt(reward.value || 0);
          });

          // Calculate real cost - based on the real cost value
          let realCost = 0;
          campaignRewards.forEach((reward) => {
            realCost += parseInt(reward.cout || 0);
          });

          return {
            ...campaign,
            consumedBudget,
            realCost,
          };
        });

        setCampaigns(campaignsWithRewards);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setLoading(false);
      }
    };

    const fetchCampaignsByEttab = async () => {
      try {
        const response = await axios.get(
          "https://trend-me.com/api/Post/" + user?.IdEtab
        );
        const transformedCampaigns = transformPostsToCampaigns(response.data);

        // Fetch rewards data for this etablissement
        const rewardsResponse = await fetch(
          "https://trend-me.com/rewards/ettab/" + user?.IdEtab
        );
        const rewardsData = await rewardsResponse.json();
        setRewards(rewardsData);

        // Calculate consumed budget and real cost with rewards data
        const campaignsWithRewards = transformedCampaigns.map((campaign) => {
          // Filter rewards for this campaign
          const campaignRewards = rewardsData.filter(
            (reward) => reward.postId?.selectedCampaignName === campaign.name
          );

          // Calculate consumed budget - based on the reward value
          let consumedBudget = 0;
          campaignRewards.forEach((reward) => {
            consumedBudget += parseInt(reward.value || 0);
          });

          // Calculate real cost - based on the real cost value
          let realCost = 0;
          campaignRewards.forEach((reward) => {
            realCost += parseInt(reward.cout || 0);
          });

          return {
            ...campaign,
            consumedBudget,
            realCost,
          };
        });

        setCampaigns(campaignsWithRewards);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setLoading(false);
      }
    };

    if (user?.type === "admin") {
      fetchCampaigns();
    } else {
      fetchCampaignsByEttab();
    }
  }, []);

  // Calculate engagement rate
  const calculateEngagementRate = (campaign) => {
    const likes = campaign.totalLikes;
    const comments = campaign.totalComments;
    const followers =
      campaign.followersGained > 0 ? campaign.followersGained : 1;

    return (((likes + comments) / followers) * 100).toFixed(2);
  };

  // Calculate cost per 1000 followers
  const calculateCostPer1000Followers = (campaign) => {
    const totalFollowersGained = campaign.followersGained || 1;
    const totalRealCost = campaign.realCost || 0;

    return ((totalRealCost / totalFollowersGained) * 1000).toFixed(2);
  };

  // Calculate days remaining
  const calculateDaysRemaining = (endDate) => {
    const daysRemaining = moment(endDate).diff(moment(), "days");
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  // Define all columns
  const allColumns = [
    {
      title: "Établissement",
      dataIndex: "etablissementName",
      key: "etablissementName",
      filters: uniqueEtablissements.map((name) => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.etablissementName === value,
    },
    {
      title: "Nom de la Campagne",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Réseaux Social",
      dataIndex: "platform",
      key: "platform",
      render: (platform) => (
        <Tag color={platform === "Instagram" ? "purple" : "blue"}>
          {platform}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color = status === "Active" ? "green" : "red";
        return <Tag color={color}>{status}</Tag>;
      },
      filters: [
        { text: "Active", value: "Active" },
        { text: "Terminé", value: "Terminé" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Jours Restant",
      key: "daysRemaining",
      render: (record) => {
        const daysRemaining = calculateDaysRemaining(record.endDate);
        return daysRemaining > 0 ? `${daysRemaining} jours` : "Terminé";
      },
    },
    {
      title: "Budget Récompense Total",
      dataIndex: "totalRewardBudget",
      key: "totalRewardBudget",
      render: (budget) => `${budget} €`,
    },
    {
      title: "Budget Consommé (Valeur)",
      dataIndex: "consumedBudget",
      key: "consumedBudget",
      render: (budget) => `${budget} €`,
    },
    {
      title: "Coût Réel",
      dataIndex: "realCost",
      key: "realCost",
      render: (cost) => `${cost} €`,
    },
    {
      title: "Nombre de Posts",
      dataIndex: "postsCount",
      key: "postsCount",
    },
    {
      title: "Followers Gagnés",
      dataIndex: "followersGained",
      key: "followersGained",
    },
    {
      title: "Coût/1000 Followers",
      key: "costPer1000Followers",
      render: (record) => `${calculateCostPer1000Followers(record)} €`,
    },
    {
      title: "Total Likes",
      dataIndex: "totalLikes",
      key: "totalLikes",
    },
    {
      title: "Total Commentaires",
      dataIndex: "totalComments",
      key: "totalComments",
    },
    {
      title: "Taux Engagement Moyen",
      key: "engagementRate",
      render: (record) => `${calculateEngagementRate(record)}%`,
    },
    {
      title: "",
      key: "",
      render: (_, record) => (
        <Button type="primary" onClick={() => setSelectedCampaign(record)}>
          Voir Paramètres
        </Button>
      ),
    },
  ];

  // Initialize visible columns on first render
  useEffect(() => {
    const initialVisibleColumns = {};
    allColumns.forEach((column) => {
      initialVisibleColumns[column.key] = true;
    });
    setVisibleColumns(initialVisibleColumns);
  }, []);

  // Filter columns based on visibility settings
  const getVisibleColumns = () => {
    return allColumns.filter((column) => visibleColumns[column.key] !== false);
  };

  // Handle column visibility toggle
  const handleColumnVisibilityChange = (columnKey, checked) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: checked,
    }));
  };

  // Column selector menu
  const columnSelectorMenu = (
    <Menu style={{ maxHeight: "400px", overflow: "auto" }}>
      {allColumns.map((column) => (
        <Menu.Item key={column.key}>
          <Checkbox
            checked={visibleColumns[column.key] !== false}
            onChange={(e) =>
              handleColumnVisibilityChange(column.key, e.target.checked)
            }
          >
            {column.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  // Filter campaigns based on status and établissement
  const getFilteredCampaigns = () => {
    return campaigns.filter((campaign) => {
      const statusMatch = statusFilter
        ? campaign.status === statusFilter
        : true;
      const etablissementMatch = etablissementFilter
        ? campaign.etablissementName === etablissementFilter
        : true;
      return statusMatch && etablissementMatch;
    });
  };

  // Modal for campaign details
  const CampaignDetailsModal = () => {
    if (!selectedCampaign) return null;

    return (
      <Modal
        title={`Paramètres de la Campagne: ${selectedCampaign.name}`}
        visible={!!selectedCampaign}
        onCancel={() => setSelectedCampaign(null)}
        footer={null}
        width={1400}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Établissement">
            {selectedCampaign.etablissementName}
          </Descriptions.Item>
          <Descriptions.Item label="Réseau Social">
            {selectedCampaign.platform}
          </Descriptions.Item>
          <Descriptions.Item label="Date Début">
            {moment(selectedCampaign.startDate).format("DD/MM/YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Date Fin">
            {moment(selectedCampaign.endDate).format("DD/MM/YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Descriptif du challenge" span={2}>
            {selectedCampaign.recompenseOption?.challengeDescription ||
              "Partage une photo ou vidéo de ton expérience"}
          </Descriptions.Item>
          <Descriptions.Item label="Condition d'accès" span={2}>
            {selectedCampaign.nbfollowersMin + "  followers" || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Mentions et hashtags" span={2}>
            {selectedCampaign.accessConditions || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Nombre total récompense distribués">
            {
              rewards.filter(
                (el) => el.postId.selectedCampaignName === selectedCampaign.name
              ).length
            }
          </Descriptions.Item>
          <Descriptions.Item label="Nombre Total récompense utilisés">
            {
              rewards.filter(
                (el) =>
                  el.postId.selectedCampaignName === selectedCampaign.name &&
                  el.status === "used"
              ).length
            }
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 20 }}>
          <h3>Paramétrage des Points</h3>
          <Descriptions bordered column={3}>
            <Descriptions.Item label="Like">
              {selectedCampaign.scoring?.like || 1}
            </Descriptions.Item>
            <Descriptions.Item label="Comment">
              {selectedCampaign.scoring?.comment || 3}
            </Descriptions.Item>
            <Descriptions.Item label="Followers">
              {selectedCampaign.scoring?.followers || 1}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <div style={{ marginTop: 20 }}>
          <h3>Niveaux de Récompense</h3>
          <Table
            dataSource={[
              {
                key: "1",
                niveau: "BuzzStarter",
                points: selectedCampaign.recompenseOption?.starterPoints || 50,
                titre:
                  selectedCampaign.recompenseOption?.starterTitle ||
                  "Récompense niveau débutant",
                valeur: `${
                  selectedCampaign.recompenseOption?.starterValue || 0
                } €`,
                valeurReelle: `${
                  selectedCampaign.recompenseOption?.starterRealValue || 0
                } €`,
              },
              {
                key: "2",
                niveau: "I Make it Trendy",
                points: selectedCampaign.recompenseOption?.trendyPoints || 100,
                titre:
                  selectedCampaign.recompenseOption?.trendyTitle ||
                  "Récompense niveau intermédiaire",
                valeur: `${
                  selectedCampaign.recompenseOption?.trendyValue || 0
                } €`,
                valeurReelle: `${
                  selectedCampaign.recompenseOption?.trendyRealValue || 0
                } €`,
              },
              {
                key: "3",
                niveau: "Pro",
                points: selectedCampaign.recompenseOption?.proPoints || 200,
                titre:
                  selectedCampaign.recompenseOption?.proTitle ||
                  "Récompense niveau pro",
                valeur: `${selectedCampaign.recompenseOption?.proValue || 0} €`,
                valeurReelle: `${
                  selectedCampaign.recompenseOption?.proRealValue || 0
                } €`,
              },
              {
                key: "4",
                niveau: "SuperStar",
                points:
                  selectedCampaign.recompenseOption?.superStarPoints || 300,
                titre:
                  selectedCampaign.recompenseOption?.superStarTitle ||
                  "Récompense niveau superstar",
                valeur: `${
                  selectedCampaign.recompenseOption?.superStarValue || 0
                } €`,
                valeurReelle: `${
                  selectedCampaign.recompenseOption?.superStarRealValue || 0
                } €`,
              },
            ]}
            columns={[
              { title: "Niveau", dataIndex: "niveau", key: "niveau" },
              { title: "Points", dataIndex: "points", key: "points" },
              { title: "Titre", dataIndex: "titre", key: "titre" },
              { title: "Valeur", dataIndex: "valeur", key: "valeur" },
              {
                title: "Valeur Réelle",
                dataIndex: "valeurReelle",
                key: "valeurReelle",
              },
            ]}
            pagination={false}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div>
      <h1 style={{ marginBottom: 16 }}>Mes Campagnes</h1>

      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="Filtrer par Statut"
          style={{ width: 200 }}
          allowClear
          onChange={(value) => setStatusFilter(value)}
        >
          <Option value="Active">Active</Option>
          <Option value="Terminé">Terminé</Option>
        </Select>
        {user?.type === "admin" && (
          <Select
            placeholder="Filtrer par Établissement"
            style={{ width: 250 }}
            allowClear
            onChange={(value) => setEtablissementFilter(value)}
          >
            {uniqueEtablissements.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
        )}

        <Dropdown
          overlay={columnSelectorMenu}
          trigger={["click"]}
          visible={columnsVisible}
          onVisibleChange={setcolumnsVisible}
        >
          <Button icon={<SettingOutlined />}>
            Afficher/Masquer les colonnes
          </Button>
        </Dropdown>
      </Space>

      <Table
        columns={getVisibleColumns()}
        dataSource={getFilteredCampaigns()}
        loading={loading}
        scroll={{ x: 1800 }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          defaultPageSize: 10,
          pageSizeOptions: ["10", "20", "50"],
        }}
      />

      <CampaignDetailsModal />
    </div>
  );
};

export default CampaignsTable;
